/** @format */

import { createSlice } from "@reduxjs/toolkit";
import { userLogin } from "./actions/userAction";

/**
 * Fill a missing D2C/PTL vertical flag from the one that IS set, at the single point the merchant
 * enters app state — so the sidebar, landing route (PrivateRoute) and dashboards all read corrected
 * flags without each file special-casing it.
 *
 * WHY NOT "missing -> false". A merchant flagged as ONE vertical, with the other flag absent, is
 * treated as not the other vertical (the absent flag becomes false). But a merchant with NEITHER
 * flag set is left exactly as-is: 754 of ~1,827 live merchants carry no vertical flag at all, and
 * forcing those to false would hide every shipment menu. So the missing flag is only ever derived
 * from an explicit `true` on the opposite one. Both unset -> unchanged -> fail open, as before.
 *
 * This normalises app state only; it never writes to the merchant record.
 */
function resolveMerchantVerticals(userInfo) {
  const m = userInfo?.merchant;
  if (!m) return userInfo;
  const d2cSet = m.isD2CMerchant === true || m.isD2CMerchant === false;
  const ptlSet = m.isPTLMerchant === true || m.isPTLMerchant === false;
  const nextD2C = d2cSet ? m.isD2CMerchant : (m.isPTLMerchant === true ? false : m.isD2CMerchant);
  const nextPTL = ptlSet ? m.isPTLMerchant : (m.isD2CMerchant === true ? false : m.isPTLMerchant);
  if (nextD2C === m.isD2CMerchant && nextPTL === m.isPTLMerchant) return userInfo;
  return { ...userInfo, merchant: { ...m, isD2CMerchant: nextD2C, isPTLMerchant: nextPTL } };
}

// initialize userToken from local storage
/* const userToken = localStorage.getItem('userToken')
  ? localStorage.getItem('userToken')
  : null */

const initialState = {
  loading: false,
  userInfo: {}, // for user object
  userToken: null, // for storing the JWT
  error: null,
  success: false, // for monitoring the login process.
  selectedUserId: null, // new field for selected user
  loginUserData: {}, // new field for selected user
  selectedMerchantOid: null, // merchant currently switched into (non-admin cross-account grant); null = home
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    logout: (state) => {
      localStorage.removeItem("userToken"); // deletes token from storage
      localStorage.removeItem("proPilotToken"); // deletes token from storage
      localStorage.removeItem("refreshToken"); // deletes refresh token from storage
      state.loading = false;
      state.userInfo = {};
      state.userToken = null;
      state.error = null;
      state.selectedUserId = null;
      state.loginUserData = {};
      state.selectedMerchantOid = null;
    },
    updateToken: (state, { payload }) => {
      state.userInfo = { ...state.userInfo, accessToken: payload };
      state.userToken = payload;
    },
    switchAccount: (state) => {
      state.userInfo = {};
      state.selectedUserId = null;
      // Clear the cross-account switch target too, so the request interceptor doesn't keep
      // stamping a stale X-Merchant-Oid on the picker's calls and the next switch's fetch.
      state.selectedMerchantOid = null;
    },
    loginUserDataSlice: (state, { payload }) => {
      state.loginUserData = payload;
    },
    login: (state, { payload }) => {
      state.userInfo = resolveMerchantVerticals(payload);
    },
    setSelectedUserId: (state, { payload }) => {
      state.selectedUserId = payload;
    },
    setSelectedMerchantOid: (state, { payload }) => {
      state.selectedMerchantOid = payload;
    },
  },
  extraReducers: {
    // login user
    [userLogin.pending]: (state) => {
      state.loading = true;
      state.error = null;
    },
    [userLogin.fulfilled]: (state, { payload }) => {
      state.loading = false;
      state.userInfo = resolveMerchantVerticals(payload);
      state.userToken = payload.userToken;
      // state.selectedUserId = payload.selectedUserId || null; // set selectedUserId from payload if present
    },
    [userLogin.rejected]: (state, { payload }) => {
      state.loading = false;
      state.error = payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const { logout, login, setSelectedUserId, setSelectedMerchantOid, switchAccount, loginUserDataSlice, updateToken } = userSlice.actions;

export default userSlice.reducer;
